<template>
  <div class="project-submission-requests">
    <p v-if="!canProjectAccess">
      {{ $t('noTabPermission') }}
    </p>
    <template v-else>
      <div class="project-submission-requests-add clearfix">
        <button
          v-if="showNewSubmission"
          class="btn btn--orange add-submission-btn"
          @click="() => newSubmission()"
        >{{ $t('action.newSubmission') }}</button>
      </div>

      <div>
        <ul
          id="project-submission-requests-accordion"
          class="accordion"
          data-accordion
          data-deep-link="true"
          data-allow-all-closed="true"
          data-multi-expand="true"
          data-update-history="true"
          data-deep-link-smudge="500"
        >
          <template v-for="(submission, index) in submissions">
            <li
              v-if="(isContractor && !isProjectRepresentative) || !isDraft(submission) || submission.actionHistory.length"
              :id="`project-sr-${index}`"
              :key="index"
              class="accordion-item"
              :class="{'is-active': activeItemId === submission.id }"
              data-accordion-item
            >
              <div
                v-if="showSrOption(index) || showContractorOption(index)"
                class="sr-options"
              >
                <dotted-dropdown :id="`project-sr-dotted-dropdown-${index}`">
                  <li v-if="showSrOption(index)">
                    <DialogSlot
                      id="project-sr-request-change-modal"
                      ref="changeRequestRef"
                      :modalBtnClass="'btn--transparent'"
                      :modalBtn="$t('action.requestChange')"
                      :afterOpenModal="resetData"
                    >
                      <div
                        slot="content"
                        :class="['slot-content', 'modal-pure-text']"
                      >
                        <span>{{ $t('hint.reopenSubmission') }}</span>
                        <HKLTextarea v-model="unacceptedComment" />
                        <p
                          v-if="unacceptedCommentHint"
                          class="text_red"
                        >
                          {{ $t('hint.requestChangeReasonEmpty') }}
                        </p>
                      </div>
                      <div slot="buttons">
                        <button
                          type="button"
                          class="btn btn--orange close-reveal-modal"
                          @click="() => requestChangeApproved('changeRequestRef')"
                        >{{ $t('action.yesContinue') }}</button>
                      </div>
                    </DialogSlot>
                  </li>
                  <li v-if="showContractorOption(index)">
                    <DialogSlot
                      id="project-sr-cancel-draft-modal"
                      ref="cancelDraftRef"
                      :modalBtnClass="'btn--transparent'"
                      :modalBtn="$t('action.cancel')"
                      :showCloseBtn="false"
                    >
                      <div
                        slot="content"
                        :class="['slot-content', 'modal-pure-text']"
                      >
                        <pre>{{ $t('hint.cancelSubmission') }}</pre>
                      </div>
                      <div slot="buttons">
                        <button
                          type="button"
                          class="btn btn--darkblue close-reveal-modal"
                          @click="() => closeModal('cancelDraftRef')"
                        >{{ $t('action.goback') }}</button>
                        <button
                          type="button"
                          class="btn btn--orange close-reveal-modal"
                          @click="() => cancelSubmission(submission, 'cancelDraftRef')"
                        >{{ $t('action.yesContinue') }}</button>
                      </div>
                    </DialogSlot>
                  </li>
                </dotted-dropdown>
              </div>
              <a
                :key="index"
                class="accordion-title"
              >
                <div
                  class="title-index row"
                  :class="{ 'add-padding-left': showSrOption(index) || showContractorOption(index) }"
                >
                  <div class="small-6 medium-6 large-6 sr-name">
                    {{ $t('project.submission.name') }} {{ (submissionsCount - index) }} {{ stampFinal(submissions[index]) }}
                  </div>
                  <div
                    v-if="getSubmStatusLabel(submission)[0]"
                    class="medium-5 column uppercase-bold doc-status"
                  >
                    <div
                      class="submission-status-rect"
                      :class="getSubmStatusLabel(submission)[1]"
                    >
                      {{ getSubmStatusLabel(submission)[0] }}
                    </div>
                  </div>
                </div>
              </a>
              <div
                :id="`project-sr-accordion-${index}`"
                class="accordion-content"
                data-tab-content
              >
                <submission
                  v-if="(isContractor && !isProjectRepresentative) && editMode(submission.status)"
                  :key="`${submission.id}-${submissionKey}`"
                  :submission="submission"
                  :companyName="companyName"
                  :disableSubmit="shouldDisableSubmit(submission.id)"
                  :draftSubmissionCounts="draftSubmissionCounts"
                  :srIndex="index"
                  :isContractor="isContractor"
                  :isFirstSubmission="isFirstSubmission"
                  :isEdit="editMode(submission.status)"
                  @refreshPage="() => refreshPage()"
                />
                <submission-view
                  v-if="(isContractor && !isProjectRepresentative) ? !editMode(submission.status) : true"
                  :submission="submission"
                  :companyName="companyName"
                  :srIndex="index"
                  :isContractor="isContractor"
                />
              </div>
            </li>
          </template>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>
import lodash from 'lodash'
import { ref, unref } from 'vue-demi'
import { mapActions, mapGetters } from 'vuex'
import { notifySubmissionAcceptance, updateInspector } from '@/api/projects/request'
import { getSubmStatusLabel } from '@/api/projects/submission'
import DialogSlot from '@/controls/DialogSlot'
import DottedDropdown from '@/controls/DottedDropdown'
import HKLTextarea from '@/controls/Input/HKLTextarea.vue'
import Submission from '@/modules/projects/submission-request/edit/Submission'
import { isFinalSubmission } from '@/modules/projects/submission-request/utils'
import SubmissionView from '@/modules/projects/submission-request/view/Submission'
import { useCanProjectAccess, useIsProjectRepresentative } from '@/pages/projects/projectInfo'
import { utils as formUtils } from '@/plugin/InputChange'
import { aclHelper } from '@/utils/aclHelper'
import { ActionTypes } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import EventBus from '@/utils/eventBus'
import { completionType, projectStatus, submissionStatus } from '@/utils/locale'

export default {
  components: {
    HKLTextarea,
    Submission,
    SubmissionView,
    DottedDropdown,
    DialogSlot,
  },
  setup () {
    let returnData = {}
    const canProjectAccess = useCanProjectAccess()
    returnData = Object.assign(returnData, {
      canProjectAccess,
    })

    const isProjectRepresentative = useIsProjectRepresentative()
    returnData = Object.assign(returnData, {
      isProjectRepresentative,
    })

    const unacceptedComment = ref('')
    const unacceptedCommentHint = ref(false)
    const resetData = () => {
      unacceptedComment.value = ''
      unacceptedCommentHint.value = false
    }
    const validateUnacceptedComment = () => {
      if (!unref(unacceptedComment) || unref(unacceptedComment).trim() === '') {
        unacceptedCommentHint.value = true
        return false
      }
      unacceptedCommentHint.value = false
      return true
    }
    returnData = Object.assign(returnData, {
      unacceptedCommentHint,
      unacceptedComment,
      resetData,
      validateUnacceptedComment,
    })
    return returnData
  },
  data () {
    return {
      draftSubmissionCounts: 0,
      submissionKey: 0,
    }
  },
  computed: {
    ...mapGetters('user', ['isContractor', 'currentUser']),
    ...mapGetters('projects', [
      'currentProject', 'hasRetention', 'purchase',
    ]),
    ...mapGetters('projectContractors', ['isMainUser', 'isSubUser']),
    submissions () {
      if (!(this.currentProject || {}).submissions) return []
      return this.currentProject.submissions
    },
    submissionsCount () {
      return this.submissions.length
    },
    companyName () {
      return (lodash.get(this.purchase, 'company.name')) || ''
    },
    isFirstSubmission () {
      return this.submissionsCount <= 1
    },
    showNewSubmission () {
      if (this.isProjectRepresentative) return false
      if (!this.isContractor || this.currentProject.status !== projectStatus.ACTIVE) return false
      if (this.submissionsCount === 0) return true

      let shouldShow = true
      for (let i = this.submissionsCount; i > 0; i -= 1) {
        const submission = this.submissions[i - 1]
        if (this.editMode(submission.status) ||
          submission.status === submissionStatus.OPEN ||
          this.isFullApproved(submission)) {
          shouldShow = false
        }
      }
      return shouldShow
    },
    allowChangeIndex () {
      let [index, submittedDate] = [null, null]
      for (let i = 0; i < this.submissionsCount; i += 1) {
        const submission = this.submissions[i]
        if (submission.status === submissionStatus.APPR) {
          const date = dateUtil.toMoment(submission.submittedDate)
          if (!submittedDate || date.isAfter(submittedDate)) {
            index = i
            submittedDate = date
          }
        }
      }
      return index
    },
    allowCancelIndex () {
      let [index, lastOrder] = [null, null]
      for (let i = 0; i < this.submissionsCount; i++) {
        const submission = this.submissions[i]
        if ([submissionStatus.DRAFT, submissionStatus.REJECTED].includes(submission.status)) {
          let order = submission.order
          if (order > lastOrder) {
            index = i
            lastOrder = order
          }
        }
      }
      return index
    },
    wapprItem () {
      const items = this.submissions.filter(submission => submission.status === submissionStatus.OPEN)
      return items.length > 0 ? items[0] : null
    },
    hasFullApprovedSub () {
      const hasFull = this.submissions.some(submission => {
        return this.isFullApproved(submission)
      })
      return hasFull
    },
    activeItemId () {
      if (this.submissions.length === 0) {
        return null
      }

      const draftItems = this.submissions.filter(submission => submission.status === submissionStatus.DRAFT || submission.status === submissionStatus.REJECTED)
      if (!this.isContractor && draftItems.length === 0) {
        return this.wapprItem ? this.wapprItem.id : this.submissions[this.allowChangeIndex || 0].id
      }
      if (draftItems.length === 0) {
        return this.submissions[0].id
      }

      this.draftSubmissionCounts = draftItems.length
      let item = null
      draftItems.forEach(submission => {
        const showDate = item ? dateUtil.toMoment(item.createdAt) : null
        const submissionDate = dateUtil.toMoment(submission.createdAt)
        item = showDate && submissionDate.isAfter(showDate) ? item : submission
      })
      return item.id
    },
  },
  destroyed () {
    EventBus.$off('refresh:project-submission-request')
    $('#project-submission-requests-accordion').off('down.zf.accordion')
    if (this.canProjectAccess && this.accordion && Object.keys(this.accordion).length) {
      this.accordion.destroy()
    }
  },
  mounted () {
    EventBus.$on('refresh:project-submission-request', async () => {
      setTimeout(this.initAccordion, 0)
      if (this.submissions.length) {
        $('#project-sr-0').addClass('is-active')
      }
    })
  },
  methods: {
    ...mapActions('projects', [
      'updateSubmissionStatus',
      'removeSubmission',
      'createSubmission',
      'loadProject',
      'updateProject',
    ]),
    showSrOption (index) {
      return aclHelper.isProjectPic() && !this.wapprItem && this.currentProject.status === projectStatus.ACTIVE && index === this.allowChangeIndex
    },
    showContractorOption (index) {
      if (!this.isContractor) return false
      if (this.isProjectRepresentative) return false
      if (index !== this.allowCancelIndex) return false
      const currentSubmission = this.submissions[index]
      if (lodash.get(currentSubmission, 'invoice.paidAt')) return false
      return this.currentProject.status === projectStatus.ACTIVE
    },

    initAccordion () {
      if (this.accordion && Object.keys(this.accordion).length > 0) {
        if ($('#project-submission-requests-accordion').childElementCount > 0) {
          this.accordion.destroy()
        }
        $('#project-submission-requests-accordion').off('down.zf.accordion')
        delete this.accordion
      }
      if (!this.accordion) {
        this.accordion = new Foundation.Accordion($('#project-submission-requests-accordion'))
        $('#project-submission-requests-accordion').on('down.zf.accordion', () => {
          Array.from($('#project-submission-requests-accordion textarea.autosize')).forEach(el =>
            formUtils.autoresize(el),
          )
        })
      }
    },

    shouldDisableSubmit (id) {
      return this.activeItemId !== id || this.wapprItem !== null || this.hasFullApprovedSub
    },
    refreshPage () {
      setTimeout(this.initAccordion, 0)
      // Change component key to reload submission component.
      this.submissionKey = Math.floor(Math.random() * 1000)
    },

    editMode (status) {
      return (status === submissionStatus.DRAFT || status === submissionStatus.REJECTED) && this.currentProject.status === projectStatus.ACTIVE
    },

    async newSubmission () {
      const projectId = this.currentProject.id
      await this.createSubmission(projectId)
      await this.loadProject(projectId)
      this.refreshPage()
    },

    closeModal (refs) {
      this.$refs[refs][0].closeModal()
    },

    stampFinal (submission) {
      const currentStatus = submission.status
      const statusForRemainingZero = [submissionStatus.OPEN, submissionStatus.APPR]
      const remainingBalance = this.getRemainingBalance(submission)
      if (isFinalSubmission(submission, this.hasRetention) && statusForRemainingZero.includes(currentStatus)) {
        // All Retention and final payment without retention order, as well as retention order is -1, consider as history data,
        // should only show FINAL in appr status.
        const historicalFinalPayment = submission.completionType !== completionType.FINAL_PARTIAL && !submission.retentionOrder
        const historicalFinalPartial = submission.retentionOrder === -1 && submission.completionType === completionType.FINAL_PARTIAL
        const historicalSubmission = historicalFinalPartial || historicalFinalPayment
        if (currentStatus === submissionStatus.APPR || (remainingBalance === 0 && !historicalSubmission)) {
          return this.$i18n.t('project.submission.finalStamp')
        }
      } else { return '' }
    },
    getRemainingBalance (submission) {
      const isAccept = submission.status === submissionStatus.APPR
      const projectAmount = lodash.get(this.purchase, 'amount', 0)
      const previousTotalAmount = this.currentProject.completedAmount
      const currentCompletedAmount = isAccept ? 0 : lodash.get(submission, 'completedAmount', 0)
      const deductionAmount = lodash.get(this.currentProject, 'deduction.amount', 0)
      const remainingBalance = Number((projectAmount - deductionAmount - currentCompletedAmount - previousTotalAmount).toFixed(2))
      return remainingBalance
    },
    async requestChangeApproved (refs) {
      if (!this.validateUnacceptedComment()) return
      const submission = this.submissions[this.allowChangeIndex]
      const status = submissionStatus.REJECTED
      const actionHistory = {
        approver: {
          id: this.currentUser.id,
          name: this.currentUser.displayName || this.currentUser.name,
          username: this.currentUser.username,
        },
        detail: { rejectReason: this.unacceptedComment },
        actionType: this.$i18n.t('action.unaccept').toUpperCase(),
      }
      const projectId = this.currentProject.id
      await this.updateSubmissionStatus({
        id: submission.id,
        status,
        actionHistory,
        action: ActionTypes.submissions.unaccept,
        releasedPo: null,
        glDate: null,
      })
      if (lodash.get(submission, 'invoice.inspectedBy')) {
        const request = { id: '' }
        await updateInspector(submission.id, request)
      }
      await this.loadProject(projectId)
      await notifySubmissionAcceptance({
        order: submission.order,
        action: this.$i18n.t('unaccepted'),
        showNote: true,
        unacceptedComment: this.unacceptedComment,
      })
      this.closeModal(refs)
      this.resetData()
      this.refreshPage()
    },

    async cancelSubmission (submission, refs) {
      await this.removeSubmission(submission.id)
      this.closeModal(refs)

      await this.loadProject(this.currentProject.id)
      this.refreshPage()
    },

    isFullApproved (submission) {
      return submission.status === submissionStatus.APPR &&
        isFinalSubmission(submission, this.hasRetention)
    },
    getSubmStatusLabel,
    isDraft (submission) {
      return submission.status === submissionStatus.DRAFT
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/project/style';
@import '~styles/popup';
@import '~styles/accordion';
.project-submission-requests-add {
  margin-bottom: 15px;
}
.add-submission-btn {
  float: right;
}

.item-line {
  margin: 0;
}

.accordion-item {
  position: relative;
  border: 1px solid #e1e1e1;
  background-color: #f1f1f1;
}
.accordion-content {
  background-color: white;
  border-bottom: 1px solid #e6e6e6;
  padding: 0 24px;
}
.accordion-title {
  background-color: white;
}

.sr-options {
  position:absolute;
  transform: translate(0, 0%);
  z-index: 1000;
  padding: 10px;

}
.no-margin-top {
  margin-top: 0;
}
.add-padding-left {
  padding-left: 15px;
}

.doc-status {
  position: relative;
}

.submission-status-rect {
  position: absolute;
  font-family: Verdana;
  font-size: 14px;
  font-style: normal;
  font-weight: bold;
  top: -5px;
  right: 0;
  text-align: center;
  padding: 5px 12px;
}

.accepted-status {
  color: #009588;
  border: solid 1px #009588;
}

.unaccepted-status {
  color: #6e7a92;
  border: solid 1px #6e7a92;
}

.rejected-status {
  color: #f44336;
  border: solid 1px #f44336;
}
.draft-status {
  color: #6e7a92;
  border: solid 1px #6e7a92;
}
#project-submission-requests-accordion .accordion-content {
  overflow: visible;
  padding-bottom: 20px;
}
</style>

<style lang="scss">
#project-sr-request-change-modal {
  font-weight: bold;
}
</style>
